import { ExternalAppId, ExternalAppStatus } from "../generated/enums"
import { IStorageObject } from "../models/imports"
import { IOmniaSession } from "../types"
import { AppFilesService } from "./appFilesService"
import { AppMachines } from "./appMachines"
import { AppSession } from "./appSession"

export interface IAppService {

    readonly session: IOmniaSession
    readonly files: AppFilesService

    refresh(revalidate?: boolean): ng.IPromise<any>

    getSupportedApps(): IAppDefinition[]

    broadcastChange(change?: string): void

    createSession(def: IAppDefinition): AppSession

    getConnections(validate?: boolean): ng.IPromise<IAppConnection[]>
    getConnection(appId: ExternalAppId): ng.IPromise<IAppConnection>

    createConnection(appId: ExternalAppId): ng.IPromise<IAppNewConnection>
    watchNewConnection(connectionId: string, onConnected: Function): Function

    cancelConnection(connectionId: string): ng.IPromise<any>
    disconnect(connectionId: string, keepData: boolean): ng.IPromise<any>

    validateBasicConnection(connectionId: string, user: string, password: string): ng.IPromise<boolean>

    getFiles(session: IAppSession): ng.IPromise<IAppFiles>
    getFiles(app: IAppDefinition): ng.IPromise<IAppFiles>

    checkSyncData(connectionId: string): ng.IPromise<boolean>

    sendFile(target: IExportTarget): ng.IPromise<any>

}


export interface IAppSession {

    readonly appId: ExternalAppId
    readonly connectionId: string
    readonly status: ExternalAppStatus | -1

    getMachines(): AppMachines

}

export interface IAppConnection {

    readonly appId: ExternalAppId
    readonly app: IAppDefinition
    readonly connectionId: string

    readonly status: ExternalAppStatus

    readonly invalidConnection: boolean

    readonly userId: number
    readonly userName: string

    readonly holding?: {
        holdingId: number,
        organisation: string,
        config?: {}
    }

}

interface IAppNewConnection extends IAppConnection {
    readonly authorizeUri: string
}

export interface IAppDefinition {

    readonly appId: ExternalAppId
    readonly name: string

    readonly connectionMode: AppConnectionMode

    readonly logo: string
    readonly icon?: string

    readonly connectPrompt: string

    readonly holdingSync?: boolean

    readonly orgs?: {

        required: boolean

        label: string

        messages: {

            resetLinks: Record<"title" | "prompt", string>

            share: Record<"title" | "prompt" | "loading" | "noMachines", string>

        }


    }

    readonly storage?: {
        readonly canSelectFolders: boolean
        readonly exportsRequireTargetFolder?: boolean
    }

    readonly machines?: boolean

    readonly remoteBoundaries?: {
        canUpdate: boolean,
        canDelete: boolean
    }

}

export enum AppConnectionMode {
    OAuth = 1,
    BasicAuth = 2
}


export interface IAppOrganisation {
    id: string;
    name: string;
}

export interface IAppConfig {
    organisations: IAppOrganisation[]
    org: string
    config?: {}
}

export interface IAppFiles {

    readonly connectionId: string
    readonly connected: boolean

    readonly name: string

    readonly canSelectFolders: boolean

    getFolderContent(path: string): ng.IPromise<IFolderContent>

    getConnectionState(): StorageBrowserState
    setConnectionState(state: StorageBrowserState): void
    refresh(): void

    readonly hasOrganisations: boolean
    getDefaultOrganisation(): IAppOrganisation

    getBoundariesSources(holdingId: number): ng.IPromise<IStorageBoundariesSource[]>

}

export interface IFolderContent {
    path: string
    content: IStorageObject[]
}

export interface IFolderPath {
    name: string
    path: string
}

export interface IStorageBoundariesSource {
    Organisation: string
    Client: string
    Farm: string
    Path: string
}

export interface IExportTarget {

    exportId: string
    connectionId: string

    orgId: string

    machines: { id: string, name: string }[]

    path: string
    holdingSubfolder: boolean

}

export interface IAppMachine {
    id: string
    name: string
    description: string
}

export interface IAppMachines {

    getMachines(orgId: string): ng.IPromise<IAppMachine[]>

}

export interface AppFilesState {
    browser: StorageBrowserState
    cache: Map<string, IFolderContent>
}

export interface StorageBrowserState {
    location: (IFolderContent & { name: string })[]
}
